// Diacritiques dans les PDF jsPDF (1.5.3) — devis, factures, chargements…
//
// Quand l'agent (ou une personne) écrit « Comandă CONFIRMATĂ » ou « ascuțite »
// dans une note interne ou une description de produit, le PDF se cassait :
//  • polices standard (Helvetica / Times / Courier, 1 octet, WinAnsi) : dès qu'un
//    caractère dépasse U+00FF et n'est pas dans WinAnsi (ă Ă ș Ș ț Ț…), jsPDF
//    réencode TOUTE la chaîne en UCS-2 → lettres espacées « C o m a n d », « þÿ »
//    en tête (fromHTML), lettre roumaine perdue ;
//  • Amiri (TTF embarquée, Identity-H) : Șș Țț à virgule souscrite n'ont pas de
//    glyphe → la lettre disparaît (« Judeul »). Voir roDiacritics.js.
//
// Branché une seule fois (import dans main.js) sur les hooks de jsPDF, donc
// valable pour tous les générateurs sans toucher à leurs appels :
//  - doc.text (enveloppé), et par là jspdf-autotable ;
//  - internal.pdfEscape : doc.fromHTML (bandeau « Nota interna ») ;
//  - splitTextToSize / getStringUnitWidth : la mesure porte sur le texte dessiné.
import jsPDF from 'jspdf'
import { toAmiriRomanian } from '@/helpers/roDiacritics.js'

// Caractères au-delà de U+00FF que WinAnsiEncoding sait quand même écrire.
const WINANSI_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ'
// Leur octet dans WinAnsiEncoding, dans le même ordre.
const WINANSI_EXTRA_BYTES = [
  0x80, 0x82, 0x83, 0x84, 0x85, 0x86, 0x87, 0x88, 0x89, 0x8a, 0x8b, 0x8c, 0x8e,
  0x91, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x98, 0x99, 0x9a, 0x9b, 0x9c, 0x9e, 0x9f
]
// Lettres latines sans décomposition Unicode (pas d'accent à retirer).
const LATIN_FALLBACK = {
  ł: 'l',
  Ł: 'L',
  đ: 'd',
  Đ: 'D',
  ı: 'i',
  ħ: 'h',
  Ħ: 'H',
  ŧ: 't',
  Ŧ: 'T',
  ŀ: 'l',
  Ŀ: 'L',
  ŋ: 'n',
  Ŋ: 'N',
  ſ: 's',
  ĸ: 'k'
}
const COMBINING_MARKS = /[\u0300-\u036f]/g // accents détachés par la décomposition NFD
const NOT_LATIN1 = /[\u0100-\uffff]/ // au moins un caractère au-delà de U+00FF

/** Lettre de base d'un caractère latin accentué (ă → a, Ț → T), sinon null. */
function baseLatin (ch) {
  const stripped = ch.normalize('NFD').replace(COMBINING_MARKS, '')
  if (stripped && stripped !== ch && !NOT_LATIN1.test(stripped)) return stripped
  return LATIN_FALLBACK[ch] || null
}

/** Texte pour une police standard (Helvetica…) : tout ramené à WinAnsi. */
export function toWinAnsi (text) {
  if (typeof text !== 'string' || !NOT_LATIN1.test(text)) return text
  let out = ''
  for (const ch of text) {
    if (ch.charCodeAt(0) <= 0xff || WINANSI_EXTRA.includes(ch)) out += ch
    else out += baseLatin(ch) || '?'
  }
  return out
}

/**
 * Octets WinAnsi (Windows-1252) pour doc.fromHTML. Il passe par internal.pdfEscape
 * SANS la table d'encodage de la police : un « — », « € » ou « … » (au-delà de
 * U+00FF) faisait encore réencoder toute la ligne en UCS-2 (« þÿ C o m a n d a »,
 * devis 10533). On écrit directement l'octet WinAnsi, que Helvetica sait dessiner.
 */
export function toWinAnsiBytes (text) {
  const winAnsi = toWinAnsi(text)
  if (typeof winAnsi !== 'string' || !NOT_LATIN1.test(winAnsi)) return winAnsi
  let out = ''
  for (const ch of winAnsi) {
    const i = WINANSI_EXTRA.indexOf(ch)
    out += i === -1 ? ch : String.fromCharCode(WINANSI_EXTRA_BYTES[i])
  }
  return out
}

/**
 * Texte pour une police TTF embarquée (Identity-H) : seules les lettres SANS
 * glyphe sont remplacées (variante à cédille, puis lettre de base) ; un
 * caractère sans équivalent reste tel quel, comme avant.
 */
export function toFontGlyphs (text, font) {
  const meta = font && font.metadata
  if (typeof text !== 'string' || !meta || typeof meta.characterToGlyph !== 'function') return text
  const hasGlyph = (ch) => meta.characterToGlyph(ch.charCodeAt(0)) > 0
  let out = ''
  for (const ch of text) {
    if (ch.charCodeAt(0) < 0x80 || hasGlyph(ch)) { out += ch; continue }
    const cedilla = toAmiriRomanian(ch)
    if (cedilla !== ch && hasGlyph(cedilla)) { out += cedilla; continue }
    const base = baseLatin(ch)
    out += base && Array.from(base).every(hasGlyph) ? base : ch
  }
  return out
}

/** Chaîne ou tableau de lignes, selon la police active. */
export function toPrintable (text, font) {
  if (Array.isArray(text)) return text.map((line) => toPrintable(line, font))
  if (typeof text !== 'string') return text
  return font && font.encoding === 'Identity-H' ? toFontGlyphs(text, font) : toWinAnsi(text)
}

let installed = false

/** Branche les hooks sur la bibliothèque jsPDF (une seule fois). */
export function installJsPdfTextSafe (lib) {
  const API = lib && lib.API
  if (installed || !API || !Array.isArray(API.events)) return
  installed = true

  // doc.text et internal.pdfEscape sont créés par instance (pas sur jsPDF.API) :
  // on les enveloppe à la naissance de chaque document.
  API.events.push([
    'initialized', function () {
      // jsPDF appelle ses abonnés avec le document pour `this`.
      // eslint-disable-next-line no-invalid-this
      const doc = this
      if (!doc || typeof doc.text !== 'function' || !doc.internal) return

      // doc.text (et jspdf-autotable) : à traiter AVANT jsPDF, qui réencode la chaîne
      // dès l'entrée (le hook preProcessText arrive trop tard pour les polices 1 octet).
      const originalText = doc.text
      doc.text = function (text, x, y, ...rest) {
        let font = null
        try { font = doc.internal.getFont() } catch (e) { font = null }
        // Ancienne signature doc.text(x, y, texte), encore utilisée dans les générateurs.
        if (typeof text === 'number' && typeof x === 'number' && (typeof y === 'string' || Array.isArray(y))) {
          return originalText.call(this, text, x, toPrintable(y, font), ...rest)
        }
        return originalText.call(this, toPrintable(text, font), x, y, ...rest)
      }

      // doc.fromHTML écrit via internal.pdfEscape, qui n'est valable que pour les polices 1 octet.
      const originalEscape = doc.internal.pdfEscape
      if (typeof originalEscape === 'function') {
        doc.internal.pdfEscape = function (text, flags) {
          return originalEscape.call(this, toWinAnsiBytes(text), flags)
        }
      }
    }
  ])

  // Mesures : même texte que celui qui sera dessiné.
  const measures = ['splitTextToSize', 'getStringUnitWidth']
  measures.forEach((name) => {
    const original = API[name]
    if (typeof original !== 'function') return
    API[name] = function (text, ...rest) {
      let font = rest[0] && rest[0].font
      if (!font) {
        try { font = this.internal.getFont() } catch (e) { font = null }
      }
      return original.call(this, toPrintable(text, font), ...rest)
    }
  })
}

installJsPdfTextSafe(jsPDF)
